import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "accessibility-options-dropdown" }
const _hoisted_2 = ["aria-expanded", "aria-label", "title"]
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", {
  class: "material-symbols-outlined accessibility-options-trigger-icon",
  "aria-hidden": "true"
}, "accessibility", -1)
const _hoisted_4 = {
  class: "material-symbols-outlined accessibility-options-trigger-caret",
  "aria-hidden": "true"
}
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = {
  class: "material-symbols-outlined accessibility-options-item-icon",
  "aria-hidden": "true"
}
const _hoisted_7 = ["aria-pressed", "aria-label"]
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", {
  class: "material-symbols-outlined accessibility-options-item-icon",
  "aria-hidden": "true"
}, "contrast", -1)
const _hoisted_9 = ["aria-label"]
const _hoisted_10 = /*#__PURE__*/_createElementVNode("span", {
  class: "material-symbols-outlined accessibility-options-item-icon",
  "aria-hidden": "true"
}, "format_size", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("div", {
  class: "accessibility-options-divider",
  role: "separator"
}, null, -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", {
  class: "material-symbols-outlined accessibility-options-item-icon",
  "aria-hidden": "true"
}, "accessibility_new", -1)

import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { useModalFocusTrap } from "@/composables/useModalFocusTrap";
import { useA11yDisplayPreferences } from "@/composables/useA11yDisplayPreferences";
import type {
  A11yColorScheme,
  A11yFontSizeLevel,
} from "@/utils/a11yDisplayPreferences";
import { isAccessibilityToolsEnabled } from "@/utils/accessibilityTools";


export default /*#__PURE__*/_defineComponent({
  __name: 'AccessibilityOptionsWidget',
  props: {
    placement: { default: "header" }
  },
  setup(__props: any) {

const accessibilityToolsEnabled = isAccessibilityToolsEnabled();



const { t } = useI18n();
const widgetRef = ref<HTMLElement | null>(null);
const isDropdownOpen = ref(false);
const dropdownTitleId = "accessibility-options-dropdown-title";

const {
  preferences,
  themeCycleUi,
  currentThemeUi,
  cycleTheme,
  toggleHighContrast,
  cycleFontSize,
} = useA11yDisplayPreferences();

const themeLabelKeys: Record<A11yColorScheme, string> = {
  light: "UI_THEME_LIGHT",
  dark: "UI_THEME_DARK",
  auto: "UI_THEME_AUTO",
};

const fontSizeLabelKeys: Record<A11yFontSizeLevel, string> = {
  0: "UI_FONT_SIZE_LEVEL_0",
  1: "UI_FONT_SIZE_LEVEL_1",
  2: "UI_FONT_SIZE_LEVEL_2",
};

const currentThemeLabel = computed(() =>
  t(`message.${themeLabelKeys[currentThemeUi.value.nextMode]}`),
);

const themeCycleLabel = computed(() =>
  t(`message.${themeLabelKeys[themeCycleUi.value.nextMode]}`),
);

const themeCycleActionLabel = computed(() =>
  t("message.UI_THEME_CYCLE_TO", { mode: themeCycleLabel.value }),
);

const contrastStateLabel = computed(() =>
  preferences.value.highContrast
    ? t("message.UI_THEME_HIGH_CONTRAST_ENABLE")
    : t("message.UI_THEME_HIGH_CONTRAST_DISABLE"),
);

const contrastToggleTargetLabel = computed(() =>
  preferences.value.highContrast
    ? t("message.UI_THEME_HIGH_CONTRAST_DISABLE")
    : t("message.UI_THEME_HIGH_CONTRAST_ENABLE"),
);

const contrastToggleActionLabel = computed(() =>
  t("message.UI_CONTRAST_TOGGLE_TO", { mode: contrastToggleTargetLabel.value }),
);

const currentFontSizeLabel = computed(() =>
  t(`message.${fontSizeLabelKeys[preferences.value.fontSizeLevel]}`),
);

const nextFontSizeLevel = computed(
  () => ((preferences.value.fontSizeLevel + 1) % 3) as A11yFontSizeLevel,
);

const nextFontSizeLabel = computed(() =>
  t(`message.${fontSizeLabelKeys[nextFontSizeLevel.value]}`),
);

const fontSizeCycleActionLabel = computed(() =>
  t("message.UI_FONT_SIZE_CYCLE_TO", { size: nextFontSizeLabel.value }),
);

const currentSettingsSummary = computed(() =>
  [
    currentThemeLabel.value,
    contrastStateLabel.value,
    currentFontSizeLabel.value,
  ].join(", "),
);

const triggerAccessibleLabel = computed(() =>
  t("message.UI_DISPLAY_CURRENT", { setting: currentSettingsSummary.value }),
);

const accessibilityStatementRoute = { name: "AccessibilityStatement" };

useModalFocusTrap(widgetRef, () => isDropdownOpen.value, {
  onEscape: () => {
    isDropdownOpen.value = false;
  },
});

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = (event: Event) => {
  const target = event.target as Node | null;
  if (isDropdownOpen.value && target && !widgetRef.value?.contains(target)) {
    isDropdownOpen.value = false;
  }
};

const onCycleTheme = () => {
  cycleTheme();
};

const onToggleContrast = () => {
  toggleHighContrast();
};

const onCycleFontSize = () => {
  cycleFontSize();
};

onMounted(() => {
  document.addEventListener("click", closeDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", closeDropdown);
});

return (_ctx: any,_cache: any) => {
  return (_unref(accessibilityToolsEnabled))
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "widgetRef",
        ref: widgetRef,
        class: _normalizeClass(["accessibility-options-widget", {
      'accessibility-options-widget--header': __props.placement === 'header',
      'accessibility-options-widget--footer': __props.placement === 'footer',
      'accessibility-options-widget--booking-footer':
        __props.placement === 'bookingFooter',
      'accessibility-options-widget--page-end': __props.placement === 'pageEnd',
    }])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["btn btn-outline-secondary btn-sm accessibility-options-trigger", { 'accessibility-options-trigger--open': isDropdownOpen.value }]),
            "aria-expanded": isDropdownOpen.value ? 'true' : 'false',
            "aria-haspopup": "menu",
            "aria-controls": "accessibility-options-dropdown",
            "aria-label": _unref(triggerAccessibleLabel),
            title: _unref(t)('message.UI_DISPLAY'),
            onClick: toggleDropdown
          }, [
            _hoisted_3,
            _createElementVNode("span", {
              class: _normalizeClass(["accessibility-options-trigger-label", {
            'd-none d-lg-inline': __props.placement === 'header',
            'd-none d-sm-inline':
              __props.placement === 'footer' ||
              __props.placement === 'pageEnd' ||
              __props.placement === 'bookingFooter',
          }])
            }, _toDisplayString(_unref(t)("message.UI_DISPLAY")), 3),
            _createElementVNode("span", _hoisted_4, _toDisplayString(isDropdownOpen.value ? "expand_less" : "expand_more"), 1)
          ], 10, _hoisted_2),
          _withDirectives(_createElementVNode("div", {
            id: "accessibility-options-dropdown",
            class: _normalizeClass(["accessibility-options-menu", {
          'accessibility-options-menu--end':
            __props.placement === 'header' || __props.placement === 'footer',
          'accessibility-options-menu--center':
            __props.placement === 'pageEnd' || __props.placement === 'bookingFooter',
          'accessibility-options-menu--up':
            __props.placement === 'footer' ||
            __props.placement === 'pageEnd' ||
            __props.placement === 'bookingFooter',
        }]),
            role: "menu",
            "aria-labelledby": dropdownTitleId
          }, [
            _createElementVNode("h2", {
              id: dropdownTitleId,
              class: "visually-hidden"
            }, _toDisplayString(_unref(t)("message.UI_DISPLAY")), 1),
            _createElementVNode("button", {
              type: "button",
              class: "accessibility-options-item",
              role: "menuitem",
              "aria-label": _unref(themeCycleActionLabel),
              onClick: onCycleTheme
            }, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(currentThemeUi).icon), 1),
              _createElementVNode("span", null, _toDisplayString(_unref(currentThemeLabel)), 1)
            ], 8, _hoisted_5),
            _createElementVNode("button", {
              type: "button",
              class: "accessibility-options-item",
              role: "menuitem",
              "aria-pressed": _unref(preferences).highContrast,
              "aria-label": _unref(contrastToggleActionLabel),
              onClick: onToggleContrast
            }, [
              _hoisted_8,
              _createElementVNode("span", null, _toDisplayString(_unref(contrastStateLabel)), 1)
            ], 8, _hoisted_7),
            _createElementVNode("button", {
              type: "button",
              class: "accessibility-options-item",
              role: "menuitem",
              "aria-label": _unref(fontSizeCycleActionLabel),
              onClick: onCycleFontSize
            }, [
              _hoisted_10,
              _createElementVNode("span", null, _toDisplayString(_unref(currentFontSizeLabel)), 1)
            ], 8, _hoisted_9),
            _hoisted_11,
            _createVNode(_unref(RouterLink), {
              class: "accessibility-options-item",
              role: "menuitem",
              to: accessibilityStatementRoute,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (isDropdownOpen.value = false))
            }, {
              default: _withCtx(() => [
                _hoisted_12,
                _createElementVNode("span", null, _toDisplayString(_unref(t)("message.A11Y_STATEMENT_TITLE")), 1)
              ]),
              _: 1
            })
          ], 2), [
            [_vShow, isDropdownOpen.value]
          ])
        ])
      ], 2))
    : _createCommentVNode("", true)
}
}

})